import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const PENDING_KEY = "ts_pending_engineer_registration";

function loadPending(routeState) {
  if (routeState?.registrationId) return routeState;
  try {
    const stored = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null");
    return stored?.registrationId ? stored : null;
  } catch {
    return null;
  }
}

export default function EngineerVerifyOtp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { engineerRegistrationResend, verifyEngineerRegistration, user } = useAuth();
  const [pending, setPending] = useState(() => loadPending(location.state));
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  if (user?.kind === "engineer") return <Navigate to="/engineer/profile" replace />;

  if (!pending) return <div className="min-h-screen flex items-center justify-center px-6 pt-24"><div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0c] p-10 text-center"><MailCheck className="w-10 h-10 text-[#e11d48] mx-auto mb-5" /><h1 className="font-[Outfit] text-2xl font-bold mb-3">Registration Not Found</h1><p className="text-sm text-zinc-400 mb-7">Complete the registration form to receive a new OTP.</p><Link to="/engineer/register" className="text-[#fb7185] font-semibold hover:underline">Back to Registration</Link></div></div>;

  const resendSeconds = Math.max(0, Math.ceil((pending.resendAt - now) / 1000));
  const expiresSeconds = Math.max(0, Math.ceil((pending.expiresAt - now) / 1000));
  const expired = expiresSeconds <= 0;

  const persist = (next) => {
    setPending(next);
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(next));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) return toast.error("Enter the 6-digit OTP from your email");
    if (expired) return toast.error("OTP has expired. Request a new code.");
    setLoading(true);
    try {
      await verifyEngineerRegistration(pending.registrationId, otp);
      sessionStorage.removeItem(PENDING_KEY);
      toast.success("Engineer account verified successfully");
      navigate("/engineer/profile", { replace: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (resendSeconds > 0 || resending) return;
    setResending(true);
    try {
      const response = await engineerRegistrationResend(pending.registrationId);
      persist({ ...pending, emailHint: response.email_hint || pending.emailHint, resendAt: Date.now() + (response.resend_after || 60) * 1000, expiresAt: Date.now() + (response.expires_in || 600) * 1000 });
      setOtp("");
      toast.success("A new OTP has been sent");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "OTP belum dapat dikirim ulang");
    } finally {
      setResending(false);
    }
  };

  const restart = () => sessionStorage.removeItem(PENDING_KEY);

  return <div className="min-h-screen flex items-center justify-center px-6 pt-24 pb-20"><form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0a0a0c] p-8 sm:p-10"><div className="w-12 h-12 rounded-xl bg-[#e11d48]/15 flex items-center justify-center mb-6"><MailCheck className="w-5 h-5 text-[#e11d48]" /></div><h1 className="font-[Outfit] text-3xl font-bold mb-2">Verify Your Email</h1><p className="text-sm text-zinc-400 mb-8">Enter the 6-digit code sent to <span className="text-zinc-200">{pending.emailHint}</span>. Your account will be created after successful verification.</p><label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Email OTP</label><input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="w-full bg-[#050505] border border-white/10 rounded-lg px-4 py-4 text-center text-2xl tracking-[0.45em] font-mono mb-3 focus:outline-none focus:border-[#e11d48]" /><div className={`text-xs mb-6 ${expired ? "text-amber-400" : "text-zinc-500"}`}>{expired ? "OTP expired. Request a new code." : `Code expires in ${Math.floor(expiresSeconds / 60)}:${String(expiresSeconds % 60).padStart(2, "0")}`}</div><button disabled={loading || expired || !/^\d{6}$/.test(otp)} className="w-full py-3.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] font-semibold flex items-center justify-center gap-2 disabled:opacity-40">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Create Account"}</button><button type="button" onClick={resend} disabled={resendSeconds > 0 || resending} className="w-full mt-4 py-3 text-sm text-[#fb7185] font-semibold disabled:text-zinc-600">{resending ? "Sending..." : resendSeconds > 0 ? `Resend in ${resendSeconds} seconds` : "Resend OTP"}</button><Link to="/engineer/register" onClick={restart} className="mt-5 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Edit Registration Details</Link></form></div>;
}
